import type { ICellRendererParams } from 'ag-grid-enterprise';
import type { CSSProperties, FunctionComponent } from 'react';
import { useEffect, useRef } from 'react';

interface IProps {
    /** The `setTooltip` handed to the cell renderer by ag-grid. */
    setTooltip: NonNullable<ICellRendererParams['setTooltip']>;
    text?: string;
    value?: unknown;
    valueFormatted?: string | null;
    /** Font size / line-height to render with. Defaults to `body-large`. */
    fontVariant?: 'body-medium' | 'body-large';
    style?: CSSProperties;
}

const truncationStyle: CSSProperties = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
};

/**
 * Truncates a Wealth Overview cell value with an ellipsis and exposes its full value through the native ag-grid tooltip,
 * but only when it is actually truncated: `shouldDisplayTooltip` is evaluated on hover against the live overflow state.
 */
export const TruncatedCellText: FunctionComponent<IProps> = ({ setTooltip, text, value, valueFormatted, fontVariant = 'body-large', style }) => {
    const ref = useRef<HTMLSpanElement>(null);
    const content = text ?? valueFormatted ?? (typeof value === 'string' ? value : '');

    useEffect(() => {
        setTooltip(content, () => {
            const el = ref.current;

            return !!el && el.scrollWidth > el.clientWidth;
        });
    }, [content, setTooltip]);

    if (!content) return null;

    return (
        <span
            ref={ref}
            style={{
                ...truncationStyle,
                fontSize: `var(--type-${fontVariant}-font-size)`,
                lineHeight: `var(--type-${fontVariant}-line-height)`,
                ...style,
            }}
        >
            {content}
        </span>
    );
};
